import { CustomerContactInfo, GateReason } from '@raptor-fe/core';
import { TranslationService } from '@raptor-fe/services';

/**
 * Single source of truth for the UI consent/enablement gate. The launcher button, the list-row
 * actions, and the widget composer all resolve through this so the surfaces can never disagree, and
 * the three reasons are never conflated (Spec AC-5). The backend re-verifies the same conditions
 * authoritatively before send.
 *
 * Ported from the mockup's `gateReason` verbatim; the only change is the `featureEnabled` boolean
 * -- production feeds it from `ApplicationContextStore.features().includes(FeatureFlagTypes.CustomerTexting)`
 * rather than the mockup's demo-harness `TenantScenario`.
 */
export function gateReason(contact: CustomerContactInfo | undefined, featureEnabled: boolean): GateReason {
    if (!featureEnabled) {
        return 'feature-off';
    }
    if (!contact || !contact.phoneNumber) {
        return 'no-phone';
    }
    if (!contact.isTextApproved) {
        return 'no-consent';
    }
    return 'ok';
}

/**
 * Localized message for a gate reason, kept as three DISTINCT strings so a disabled surface always
 * names the real cause. Resolved through TranslationService with literal keys so the build-time
 * extractor can register them (Feature 'Customer Messaging').
 */
export function gateMessage(reason: GateReason, translationService: TranslationService): string {
    switch (reason) {
        case 'feature-off':
            return translationService.getTranslation(
                'Customer Messaging',
                'Conversation Widget',
                'Customer texting is not enabled for this location.',
            );
        case 'no-consent':
            return translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'Customer has not approved texting.');
        case 'no-phone':
            return translationService.getTranslation('Customer Messaging', 'Conversation Widget', 'No phone number on file for this customer.');
        default:
            return '';
    }
}
